import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import type { TreeItem } from '@/lib/tree/types';
import { atom, useAtom } from 'jotai';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Button } from '../Button/Button';

const selectedItemsAtom = atom<TreeItem[]>([]);

export const useSelectedItems = () => {
  const [selectedItems, setSelectedItems] = useAtom(selectedItemsAtom);

  const selectItem = (item: TreeItem) => {
    const isSelected = selectedItems.some((i) => i.id === item.id);
    if (isSelected) {
      setSelectedItems(selectedItems.filter((i) => i.id !== item.id));
      return;
    }
    setSelectedItems([...selectedItems, item]);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedItems([]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItems]);

  return {
    selectedItems,
    setSelectedItems,
    selectItem,
  };
};

const SelectMenu = () => {
  const [deleteClicked, setDeleteClicked] = useState(false);
  const { selectedItems, setSelectedItems } = useSelectedItems();

  const { mutateDelete: deleteChatbot } = useDeleteChatbot({
    onMutate: () => {
      setDeleteClicked(false);
      setSelectedItems([]);
    },
  });

  const open = selectedItems.length > 0;

  const selectMenuVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 30 },
  };

  function handleDeselect() {
    setSelectedItems([]);
  }

  const handleDeleteClick = async () => {
    if (deleteClicked) {
      const chatbotsToDelete = selectedItems.map((item) => item.id);
      deleteChatbot(chatbotsToDelete.map((id) => ({ id: id as string })));
      setDeleteClicked(false);
      return;
    }

    setDeleteClicked(true);
  };

  useEffect(() => {
    if (selectedItems?.length === 0) {
      setDeleteClicked(false);
    }
  }, [selectedItems]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={selectMenuVariants}
          transition={{ duration: 0.1 }}
          className="pointer-events-none fixed bottom-0 w-screen left-0 right-0 flex justify-center p-8 z-[999]"
        >
          <div className="flex flex-row items-center justify-between pl-4 pr-2 py-2 min-w-[420px] max-w-full bg-popover rounded-lg card-shadow closer-shadow transition duration-300 ease-in-out translate-y-0 opacity-100 pointer-events-auto select-none">
            <div className="text-sm">{selectedItems.length} selected</div>

            <div className="flex flex-row gap-2">
              <Button variant="secondary" size="xs" onClick={handleDeselect}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteClick}
                variant="secondary"
                size="xs"
                className={
                  deleteClicked
                    ? 'bg-destructive text-destructive-solid hover:bg-destructive hover:text-destructive-solid'
                    : 'hover:bg-muted'
                }
              >
                {deleteClicked ? 'Confirm' : 'Delete'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SelectMenu;
